
/// <reference path="../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../frontend/dispatcher';
import { TaskListResultStatus, TaskList } from './TaskList';
import { ITranscription } from './interfaces/ITranscriptionManager';
import { IAuthenticationProvider } from './interfaces/IAuthenticationProvider';
import * as DocumentStorage from './office-js/DocumentStorage';
import { IOneDriveUploadTaskResult, OneDriveUploadTask } from './OneDriveUploadTask';
import { TranscriptionStorageType, IStoredTranscription } from './interfaces/IStoredTranscription';
import { LoadingActionTypes, LoadingStatus, LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage } from '../frontend/actions/LoadingActions';
import { IRealTimeTranscriptionConnection } from './interfaces/IRealTimeTranscriptionConnection';
import { IForegroundTask } from './interfaces/IForegroundTask';
import { LogEvents } from '../utils/LogEvents';
import { ErrorStringFromException, wait } from '../utils/utils';
import { TranscriptionMonthlyQuota } from './TranscriptionMonthlyQuota';
import { CreateTranscriptionSourceFileProvider } from './TranscriptionSourceFileFactory';
import { ITranscriptionSourceFileProvider } from './interfaces/ITranscriptionSourceFileProvider';
import { FileUploadKeys } from './telemetry/FileUploadKeys';
import { DurationMeasureType } from './interfaces/ILogEvent';
import { LogError, LogErrorAndThrow, ShouldLogErrorAsWarning } from '../utils/ErrorTelemetry';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { FileUploadForegroundTaskError, FileUploadForegroundTaskErrors } from './FileUploadForegroundTaskErrors';
import { FrontDoorFileUpload } from './FrontDoorFileUpload';
import { IsFastFileUploadEnabled } from './office-js/HostAppSettings';

function SetLoadingPercentDone(percentDone: number | null) {
    // Do not show upload progress in fast file upload, since we don't have a way to get a transcription process progress.
    if (IsFastFileUploadEnabled())
        return;

    Dispatcher.dispatch({
        type: LoadingActionTypes.SetPercentDoneAction,
        payload: percentDone
    });
}

function SetLoadingStatus(status: LoadingStatus) {
    Dispatcher.dispatch({
        type: LoadingActionTypes.SetStatusAction,
        payload: status
    });
}

// This class contains the logic we execute when the Loading page is shown to the user in the File Upload scenario.
export class FileUploadForegroundTask implements IForegroundTask {
    private file: File;
    private authProvider: IAuthenticationProvider;
    private history: any;
    private taskList: TaskList<any>;
    private realTimeConnectionTaskList: TaskList<any>;
    private oneDriveUploadTask: OneDriveUploadTask;
    private realTimeConnection: IRealTimeTranscriptionConnection;
    private fileDurationSeconds: number;
    private frontDoorUpload: FrontDoorFileUpload;

    private readonly transcriptionMonthlyQuotaWaitTime: number = 10000;
  
    constructor(file: File, authProvider: IAuthenticationProvider, history: any, realTimeTranscriptionConnection: IRealTimeTranscriptionConnection) {
      this.file = file;
      this.authProvider = authProvider;
      this.history = history;
      this.realTimeConnection = realTimeTranscriptionConnection;
      LogEvents.FileUpload.Start();
      LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.TotalUploadDuration);
    }

    private CleanUpTaskState() {
        this.taskList = null;
        this.realTimeConnectionTaskList = null;
        this.oneDriveUploadTask = null;
        this.realTimeConnection = null;
        this.frontDoorUpload = null;
    }

    private onSendFileProgress(endTimestamp: number) {
        // During real-time connection, show 5% to 95% progress indicator.
        const percentDone = (endTimestamp / this.fileDurationSeconds) * 0.9 + 0.05;
        SetLoadingPercentDone(percentDone);
    }
  
    public async Execute(preauthenticatePromise: Promise<void>): Promise<void> {
      try {
          // Show Transcribing status and 0% progress indicator
          SetLoadingStatus(LoadingStatus.TranscribingAudio);
          SetLoadingPercentDone(0);

          await preauthenticatePromise;
      
          this.taskList = this.GetFileUploadTaskList();
          const result = await this.taskList.ExecuteTaskListSequentially();

          this.CleanUpTaskState();
      
          // If the upload was canceled, we are done.
          if (result.status == TaskListResultStatus.Canceled) {
            LogEvents.FileUpload.End();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: 'User canceled', isFinalResponse: true });
            return;
          }
      
          await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(result.result, this.history);

          // Show 100% progress indicator
          SetLoadingPercentDone(1);

          LogEvents.FileUpload.End();
      } catch (e) {
          await this.Cancel();

          if (ShouldLogErrorAsWarning(e)) {
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: 'Cancelled because of a warning: ' + ErrorStringFromException(e), isFinalResponse: true });
            LogEvents.FileUpload.End();
          } else {
            const failureReason = "Error uploading file. " + ErrorStringFromException(e);
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReason, isFinalResponse: true });
            LogEvents.FileUpload.End([{ key: FileUploadKeys.FailureReason, value: failureReason }]);
          }

          LogErrorAndThrow(LogEventNames.FileUpload, e);
      }
    }
  
    public async Cancel(): Promise<void> {
        try {
            if (this.taskList != null)
                this.taskList.Cancel();

            if (this.oneDriveUploadTask != null)
                await this.oneDriveUploadTask.Cancel();

            if (this.realTimeConnection != null) {
                await this.realTimeConnection.CloseConnection();
            }

            if (this.realTimeConnectionTaskList != null)
                this.realTimeConnectionTaskList.Cancel();

            if (this.frontDoorUpload != null) {
                this.frontDoorUpload.Cancel();
            }

            this.CleanUpTaskState();
        } catch (e) {
            this.CleanUpTaskState();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: `Failed when cancelling foreground task: ${ErrorStringFromException(e)}`, isFinalResponse: true });
            LogErrorAndThrow(LogEventNames.FileUpload, e);
        }
    }

    private async AddUploadMinutesToHost() {
        let didAddUploadMinutes: boolean = false;
        LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.AddMinutesUsedDuration);
        const addMinutesUsedPromise = TranscriptionMonthlyQuota.GetInstance().AddMinutesUsed(Math.floor(this.fileDurationSeconds / 60)).then(() => {
            didAddUploadMinutes = true;
        });

        await Promise.race([wait(this.transcriptionMonthlyQuotaWaitTime), addMinutesUsedPromise]).then(() => {
            if (!didAddUploadMinutes)
                LogError(LogEventNames.FileUpload, "AddMinutesUsed promise timed out");
        });

        LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.AddMinutesUsedDuration);
    }

    private GetRealTimeConnectionUploadTaskList(): TaskList<void | ITranscription> {
        return new TaskList<void | ITranscription>([
            //  1. Initialize real-time connection
            async (): Promise<void> => {
                await this.realTimeConnection.InitializeConnection();
            },
            
            //  2. Send file through real-time connection.
            async (): Promise<void> => {
                try {
                    await this.realTimeConnection.SendFile(this.file, (endTimestamp: number) => {
                        this.onSendFileProgress(endTimestamp);
                    }/*onProgressUpdate*/, (audioDurationSeconds: number) => {
                        // Show 3% progress indicator
                        SetLoadingPercentDone(0.03);
                        this.fileDurationSeconds = audioDurationSeconds;
                        LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioLengthSeconds, value: this.fileDurationSeconds}]);
                    }/*onAudioDurationReceived*/);
                } catch (e) {
                    if (e instanceof DOMException && e.name == 'EncodingError')
                        throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.UnsupportedFileFormat);
                    
                    LogErrorAndThrow(LogEventNames.FileUpload, e);
                }
            },

            //  3. Get the final transcript.
            async (): Promise<ITranscription> => this.realTimeConnection.EndSessionAndGetFinalTranscript(),

            //  4. Close real-time connection
            async (transcription: ITranscription): Promise<ITranscription> => {
                await this.realTimeConnection.CloseConnection();
                return transcription;
            }
          ]);
    }
    
    // Executes the tasks required for the file upload scenario.
    private GetFileUploadTaskList(): TaskList<any> {
        return new TaskList< void | ITranscription | IStoredTranscription | ITranscriptionSourceFileProvider >([
            // Tasks to transcribe file --->
            //  1. Do the upload
            async (): Promise<ITranscription> => {
                LogEvents.FileUpload.AddData([
                    { key: FileUploadKeys.AudioFileSizeBytes, value: this.file.size},
                    { key: FileUploadKeys.AudioFileExtension, value: this.file.name.split('.').pop()}]);

                LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.TranscriptionProcessDuration);

                let transcription = null;
                if (IsFastFileUploadEnabled()) {
                    this.fileDurationSeconds = await UNSAFE_GetAudioFileDuration(this.file);
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioLengthSeconds, value: this.fileDurationSeconds}]);
                    this.frontDoorUpload = new FrontDoorFileUpload();
                    transcription = await this.frontDoorUpload.UploadFileAndGetTranscript(this.file);
                } else {
                    this.realTimeConnectionTaskList = this.GetRealTimeConnectionUploadTaskList()
                    const result = await this.realTimeConnectionTaskList.ExecuteTaskListSequentially();
                    transcription = <ITranscription>result.result;
                }

                LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.TranscriptionProcessDuration);
                LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: null, isFinalResponse: true});
                return transcription;
            },

            // <--- Transcribing file ends

            // Upload the file to OneDrive and create transcription manager.
            async (transcription: ITranscription): Promise<IStoredTranscription> => {
                // Show 95% progress indicator
                SetLoadingPercentDone(0.95);

                // change status to uploading file
                SetLoadingStatus(LoadingStatus.UploadingFile);

                this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, TranscriptionStrings.FolderNameToUploadAudioFiles, this.file);
                const uploadResult: IOneDriveUploadTaskResult = await this.oneDriveUploadTask.UploadFile();

                if (uploadResult == null || uploadResult.oneDriveItem == null)
                    throw new Error("UploadFile returned a null oneDriveItem");

                return {
                    storageType: TranscriptionStorageType.DocumentStorage,
                    descriptor:  {
                        transcription,
                        audioOneDriveItem: uploadResult.oneDriveItem,
                    }
                };
            },

            // Write stored transcription to document storage.
            async (storedTranscription: IStoredTranscription): Promise<ITranscriptionSourceFileProvider> => {
                // Show 97% progress indicator
                SetLoadingPercentDone(0.97);

                await DocumentStorage.WriteStoredTranscription(storedTranscription);
                return CreateTranscriptionSourceFileProvider(storedTranscription, this.authProvider);
            },

            // Add upload minutes to the host.
            async (transcriptionSourceFileProvider: ITranscriptionSourceFileProvider): Promise<ITranscriptionSourceFileProvider> => {
                // Show 98% progress indicator
                SetLoadingPercentDone(0.98);

                await this.AddUploadMinutesToHost();
                return transcriptionSourceFileProvider;
            }
        ]);
    }
  }

// Gets the duration of the passed audio file
// IMPORTANT: This function is marked as UNSAFE_ because it has a file limit of 261MB.
// This is because setting the source of a media tag uses data URLs under
// the covers and data URLs are limited to 261MB. The firefox code-path does not have this limitation, but it decodes the file and requires it
// to fit "twice" (both pre-decoded and decoded) in memory.
// I'm keeping this in the Fast Upload code path to avoid breaking it, but for Fast File upload, where decoding won't happen in the client,
// we need FrontDoor (or OneDrive) to give us the length of the recording. OC:4417306 tracks this. 
function UNSAFE_GetAudioFileDuration(file: File): Promise<number> {
    // Firefox has a bug where it reports incorrect durations in audio tags (https://bugzilla.mozilla.org/show_bug.cgi?id=1441829)
    // so decode the file and get the audio duration.
    if (navigator.userAgent.indexOf("Firefox") > -1) {
        const fileReader = new FileReader();

        const promise = new Promise<number>((resolve, reject) => {
          fileReader.onload = async () => {
            try {
              const packets = <ArrayBuffer>fileReader.result;
              const audioCtx = new AudioContext();
              const audioBuffer: AudioBuffer = await audioCtx.decodeAudioData(packets);
              resolve(audioBuffer.duration);
            } catch (e) {
              reject(e);
            }
          };

          fileReader.onabort = fileReader.onerror = () => {
            reject(new Error('fileReader.onerror'));
          };
        });

        fileReader.readAsArrayBuffer(file);
        return promise;
    }

    // In all other browsers just create a temporary
    // audio tag and set the file data as its source.
    return new Promise((resolve, reject) => {
        let audioElement = document.createElement("audio");
        audioElement.onloadedmetadata = () => {
            const audioDuration = audioElement.duration;
            audioElement = null;
            resolve(audioDuration);
        };

        const reader = new FileReader();

        reader.onload = () => {
            audioElement.src = <string>(reader.result);
        };

        audioElement.onerror = reader.onerror = (e: any) => {
            audioElement = null;

            if (e != null && e.currentTarget != null)
                reject(e.currentTarget.error);
            else
                reject(new Error('AudioElement.onerror'));
        };
 
        reader.readAsDataURL(file);
    });
}